import { Autocomplete, Box, Paper, TextField, Typography } from "@mui/material";

type EntitySelectionPanelProps<T> = {
  title: string;
  disabled: boolean;
  isLocked: boolean;
  options: T[];
  value: T[];
  inputValue: string;
  placeholder: string;
  noOptionsText: string;
  getId: (item: T) => string;
  getPrimary: (item: T) => string;
  getSecondary: (item: T) => string | undefined;
  onInputChange: (value: string) => void;
  onAdd: (item: T) => void;
  onSetValue: (items: T[]) => void;
  onPaste: (text: string) => void;
};

const EntitySelectionPanel = <T,>({
  title,
  disabled,
  isLocked,
  options,
  value,
  inputValue,
  placeholder,
  noOptionsText,
  getId,
  getPrimary,
  getSecondary,
  onInputChange,
  onAdd,
  onSetValue,
  onPaste,
}: EntitySelectionPanelProps<T>) => (
  <Paper
    elevation={2}
    sx={{ p: 3, opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto" }}
  >
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>
    <Autocomplete
      multiple
      disabled={disabled || isLocked}
      options={options}
      value={value}
      inputValue={inputValue}
      filterOptions={(items) => items}
      getOptionLabel={getPrimary}
      isOptionEqualToValue={(option, selected) => getId(option) === getId(selected)}
      noOptionsText={noOptionsText}
      onInputChange={(_event, nextValue, reason) => {
        if (reason === "input") {
          onInputChange(nextValue);
        }
      }}
      onChange={(_event, nextValue, reason) => {
        if (reason === "selectOption") {
          const added = nextValue.at(-1);
          if (added) {
            onAdd(added);
          }
        } else {
          onSetValue(nextValue);
        }
      }}
      renderOption={(props, option) => (
        <li {...props} key={getId(option)}>
          <Box>
            <Typography variant="body2">{getPrimary(option)}</Typography>
            {getSecondary(option) && (
              <Typography variant="caption" color="textSecondary">
                {getSecondary(option)}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={placeholder}
          onPaste={(event) => {
            event.preventDefault();
            onPaste(event.clipboardData.getData("text"));
          }}
        />
      )}
    />
  </Paper>
);

export default EntitySelectionPanel;
